import React from 'react';
import PropTypes from 'prop-types';
import Progress from '../ui/Progress';
import Tooltip from '../ui/Tooltip';
import { cx } from '../ui/classNames';
import {
    describeRuleResult,
    formatRuleLimit,
    formatRuleValue,
} from './optimizerRuleRegistry';

const WIDTH = 132;
const HEIGHT = 22;
const PAD = 7;
const MID = HEIGHT / 2;
const COINCIDENT_PX = 4;

// The optimized marker's fill: the accent, or the danger colour on a breach.
export const OPTIMIZED_MARKER_CLASS_NAMES = Object.freeze({
    normal: 'fill-accent',
    exceeds: 'fill-ui-danger',
});

const finite = (value) => value != null && Number.isFinite(Number(value));

// One scale per row for both markers and every limit. The domain always reaches
// the largest value, so a breach is shown rather than clipped.
export function limitTrackDomain({ form, limit, draftLimit = null, baseline, optimized }) {
    const values = [limit, draftLimit, baseline, optimized].filter(finite).map(Number);
    if (form === 'min') {
        const lo = Math.min(0, ...values) * 1.12;
        const hi = Math.max(0, ...values) * 1.12;
        return lo === hi ? [0, 1] : [lo, hi];
    }
    const extent = Math.max(
        0,
        ...values.map(Math.abs),
    );
    const span = extent > 0 ? extent * 1.12 : 1;
    return form === 'abs_max' ? [-span, span] : [0, span];
}

function TooltipLines({ lines }) {
    return (
        <span className="grid gap-0.5">
            {lines.filter(Boolean).map((line) => (
                <span key={line} className="block text-[0.75rem] leading-[1.66]">{line}</span>
            ))}
        </span>
    );
}

TooltipLines.propTypes = { lines: PropTypes.arrayOf(PropTypes.string).isRequired };

// Where the baseline and optimized values sit relative to the rule's limit: a
// before/after pair on one numeric scale, not progress toward a target.
export default function LimitTrack({
    evaluation,
    rule,
    label,
    baselineLabel,
    draftLimit = null,
}) {
    if (!evaluation?.configured || !finite(evaluation.limit)) {
        return <span className="text-ui-disabled-text">—</span>;
    }
    const { form, unit } = rule;
    const { limit, baseline, optimized, applied } = evaluation;
    const showDraft = finite(draftLimit) && Number(draftLimit) !== Number(limit);
    const [lo, hi] = limitTrackDomain({
        form,
        limit,
        draftLimit: showDraft ? draftLimit : null,
        baseline,
        optimized,
    });
    const x = (value) => PAD + ((Number(value) - lo) / (hi - lo)) * (WIDTH - 2 * PAD);
    const limitPositions = form === 'abs_max' ? [-limit, limit] : [limit];
    const draftPositions = showDraft
        ? (form === 'abs_max' ? [-draftLimit, draftLimit] : [draftLimit])
        : [];
    const bandStart = x(form === 'min' ? limit : form === 'abs_max' ? -limit : 0);
    const bandEnd = x(form === 'min' ? hi : limit);
    const exceeds = evaluation.status === 'exceeds';
    const baselineX = finite(baseline) ? x(baseline) : null;
    const optimizedX = finite(optimized) ? x(optimized) : null;
    const coincident = baselineX != null
        && optimizedX != null
        && Math.abs(baselineX - optimizedX) < COINCIDENT_PX;
    const description = describeRuleResult(evaluation, rule);
    const limitText = formatRuleLimit(limit, rule);
    const summary = [
        `${label}: optimized ${formatRuleValue(optimized, unit)}`,
        `${baselineLabel} ${formatRuleValue(baseline, unit)}`,
        `limit ${limitText}${applied ? '' : ' (not applied)'}`,
    ].join(', ');

    return (
        <Tooltip
            content={(
                <TooltipLines
                    lines={[
                        `Limit: ${limitText}${applied ? '' : ' (not applied)'}`,
                        showDraft
                            ? `Edited limit: ${formatRuleLimit(draftLimit, rule)} (applies on the next run)`
                            : null,
                        `${baselineLabel}: ${formatRuleValue(baseline, unit)}`,
                        `Optimized: ${formatRuleValue(optimized, unit)}`,
                        description?.headroom ?? null,
                        description?.usage ?? null,
                        `Scale: ${formatRuleValue(lo, unit)} to ${formatRuleValue(hi, unit)}`,
                    ]}
                />
            )}
        >
            <svg
                role="img"
                aria-label={summary}
                width={WIDTH}
                height={HEIGHT}
                viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                className="ml-auto block overflow-visible"
            >
                <line x1={x(lo)} x2={x(hi)} y1={MID} y2={MID} strokeWidth={1} className="stroke-hairline" />
                <rect
                    data-testid="limit-track-allowed-range"
                    x={bandStart}
                    y={MID - 4}
                    width={Math.max(bandEnd - bandStart, 0)}
                    height={8}
                    rx={2}
                    className={applied ? 'fill-accent/[0.12]' : 'fill-accent/[0.05]'}
                />
                {form === 'abs_max' && (
                    <line
                        x1={x(0)}
                        x2={x(0)}
                        y1={MID - 5}
                        y2={MID + 5}
                        strokeWidth={1}
                        className="stroke-ui-disabled-text"
                    />
                )}
                {limitPositions.map((value) => (
                    <line
                        key={`limit-${value}`}
                        data-testid="limit-track-limit"
                        x1={x(value)}
                        x2={x(value)}
                        y1={MID - 8}
                        y2={MID + 8}
                        strokeWidth={2}
                        strokeLinecap="round"
                        className={applied ? 'stroke-content-primary' : 'stroke-ui-disabled-text'}
                    />
                ))}
                {draftPositions.map((value) => (
                    <line
                        key={`draft-${value}`}
                        data-testid="limit-track-draft-limit"
                        x1={x(value)}
                        x2={x(value)}
                        y1={MID - 8}
                        y2={MID + 8}
                        strokeWidth={1.5}
                        strokeDasharray="2 2"
                        className="stroke-content-secondary"
                    />
                ))}
                {baselineX != null && optimizedX != null && !coincident && (
                    <line
                        x1={baselineX}
                        x2={optimizedX}
                        y1={MID}
                        y2={MID}
                        strokeWidth={2}
                        className="stroke-accent/[0.45]"
                    />
                )}
                {baselineX != null && (
                    <circle
                        data-testid="limit-track-baseline"
                        cx={baselineX}
                        cy={MID}
                        r={coincident ? 7 : 4.5}
                        strokeWidth={2}
                        className="fill-surface stroke-content-primary"
                    />
                )}
                {optimizedX != null && (
                    <path
                        data-testid="limit-track-optimized"
                        d={`M ${optimizedX} ${MID - 5.5} L ${optimizedX + 5.5} ${MID} L ${optimizedX} ${MID + 5.5} L ${optimizedX - 5.5} ${MID} Z`}
                        strokeWidth={2}
                        strokeLinejoin="round"
                        className={cx(
                            'stroke-surface',
                            OPTIMIZED_MARKER_CLASS_NAMES[exceeds ? 'exceeds' : 'normal'],
                        )}
                    />
                )}
            </svg>
        </Tooltip>
    );
}

LimitTrack.propTypes = {
    evaluation: PropTypes.shape({
        configured: PropTypes.bool,
        applied: PropTypes.bool,
        status: PropTypes.string,
        limit: PropTypes.number,
        baseline: PropTypes.number,
        optimized: PropTypes.number,
        detail: PropTypes.object,
    }),
    rule: PropTypes.shape({
        unit: PropTypes.oneOf(['usd', 'percent', 'bps', 'count', 'days']).isRequired,
        form: PropTypes.oneOf(['max', 'abs_max', 'min']).isRequired,
    }).isRequired,
    label: PropTypes.string.isRequired,
    baselineLabel: PropTypes.string.isRequired,
    draftLimit: PropTypes.number,
};

// The names-changed rule's result: the count against its maximum as a bar.
export function NamesChangedTrack({ changed, limit }) {
    let value = changed > 0 ? 100 : 0;
    if (limit > 0) value = Math.min(100, (changed / limit) * 100);
    return (
        <span className="block min-w-[132px]">
            <span className="text-[0.75rem] font-bold leading-[1.66]">
                {changed} changed · max {limit}
            </span>
            <Progress value={value} aria-label="Names changed versus maximum" className="mt-[3px]" />
        </span>
    );
}

NamesChangedTrack.propTypes = {
    changed: PropTypes.number,
    limit: PropTypes.number,
};
